'use client'

import { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo, ReactNode } from 'react'
import { Socket } from 'socket.io-client'
import { getSocket, disconnectSocket, joinBoard as joinBoardRoom, leaveBoard as leaveBoardRoom } from '@/lib/socket-client'

interface SocketContextType {
  socket: Socket | null
  isConnected: boolean
  joinBoard: (boardId: string) => void
  leaveBoard: (boardId: string) => void
}

const SocketContext = createContext<SocketContextType>({
  socket: null,
  isConnected: false,
  joinBoard: () => {},
  leaveBoard: () => {},
})

export function useSocket() {
  return useContext(SocketContext)
}

export function SocketProvider({ children }: { children: ReactNode }) {
  const [socket, setSocket] = useState<Socket | null>(null)
  // Все изменяемые значения в ref — провайдер ре-рендерится только при setSocket()
  const isConnectedRef = useRef(false)
  const statusRef = useRef<'init' | 'fetching' | 'connecting' | 'connected' | 'error' | 'no-auth'>('init')
  const errorMsgRef = useRef<string | null>(null)
  const currentBoardRef = useRef<string | null>(null)
  const reconnectCountRef = useRef(0)

  useEffect(() => {
    let mounted = true

    async function init() {
      try {
        statusRef.current = 'fetching'
        console.log('[SocketProvider] Starting socket initialization...')

        const basePath = process.env.NEXT_PUBLIC_BASE_PATH || ''
        console.log('[SocketProvider] Fetching ws-token from:', `${basePath}/api/auth/ws-token`)

        const res = await fetch(`${basePath}/api/auth/ws-token`)
        console.log('[SocketProvider] ws-token response status:', res.status)

        if (!res.ok) {
          console.log('[SocketProvider] No auth token, skipping connection')
          if (mounted) {
            statusRef.current = 'no-auth'
            errorMsgRef.current = `HTTP ${res.status}`
          }
          return
        }

        const { token } = await res.json()
        console.log('[SocketProvider] Got token:', token ? `${token.substring(0, 10)}...` : 'null')

        if (!token) {
          console.log('[SocketProvider] Empty token, skipping connection')
          if (mounted) {
            statusRef.current = 'no-auth'
            errorMsgRef.current = 'Empty token'
          }
          return
        }

        statusRef.current = 'connecting'
        const s = await getSocket()
        
        if (!mounted) return
        if (!s) {
          console.log('[SocketProvider] getSocket() returned null')
          statusRef.current = 'error'
          errorMsgRef.current = 'getSocket returned null'
          return
        }

        console.log('[SocketProvider] Socket object obtained, connected:', s.connected)
        setSocket(s)
        isConnectedRef.current = s.connected

        if (s.connected) {
          statusRef.current = 'connected'
          errorMsgRef.current = null
        }

        const onConnect = () => {
          console.log('[SocketProvider] Socket CONNECTED! ID:', s.id)
          reconnectCountRef.current = 0
          if (mounted) {
            isConnectedRef.current = true
            statusRef.current = 'connected'
            errorMsgRef.current = null
          }
          // Переподключаемся к текущей доске при реконнекте
          if (currentBoardRef.current) {
            console.log('[SocketProvider] Rejoining board:', currentBoardRef.current)
            s.emit('join-board', currentBoardRef.current)
          }
        }
        const onDisconnect = (reason: string) => {
          reconnectCountRef.current++
          const count = reconnectCountRef.current
          console.log(`[SocketProvider] Socket DISCONNECTED (#${count}):`, reason)
          if (mounted) {
            isConnectedRef.current = false
            // НЕ меняем статус — остаётся 'connected'
            // Реконнект происходит автоматически, пользователь не видит мигания
          }
        }
        const onConnectError = (err: Error) => {
          if (err.message === 'timeout' || err.message === 'ping timeout') return
          console.error('[SocketProvider] Connection ERROR:', err.message)
          if (mounted) {
            statusRef.current = 'error'
            errorMsgRef.current = err.message
          }
        }

        s.on('connect', onConnect)
        s.on('disconnect', onDisconnect)
        s.on('connect_error', onConnectError)

        // Очистка при размонтировании
        return () => {
          s.off('connect', onConnect)
          s.off('disconnect', onDisconnect)
          s.off('connect_error', onConnectError)
        }
      } catch (err) {
        console.error('[SocketProvider] Init error:', err)
        if (mounted) {
          statusRef.current = 'error'
          errorMsgRef.current = err instanceof Error ? err.message : String(err)
        }
      }
    }

    init()

    return () => {
      mounted = false
    }
  }, [])

  const joinBoard = useCallback((boardId: string) => {
    currentBoardRef.current = boardId
    joinBoardRoom(boardId)
  }, [])

  const leaveBoard = useCallback((boardId: string) => {
    if (currentBoardRef.current === boardId) {
      currentBoardRef.current = null
    }
    leaveBoardRoom(boardId)
  }, [])

  // Стабилизируем значение контекста — socket устанавливается один раз и не меняется
  // isConnected больше не в зависимостях — реконнект не создаёт новый объект контекста
  const contextValue = useMemo(() => ({
    socket,
    isConnected: isConnectedRef.current,
    joinBoard,
    leaveBoard,
  }), [socket, joinBoard, leaveBoard])

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  )
}
